import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '../../../../components/ui/button';
import { useState } from 'react';
import { Input } from '../../../../components/ui/input';
import brandbookService from '../../services/brandbookApi';
import { toast } from 'react-hot-toast';
import { SaveBrandbookRequest } from 'features/brandbook/types';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useNavigate } from 'react-router-dom';

interface BrandbookSaveAsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandbook: {
    companyName: string;
    slogan: string;
    brandValues: Array<{
      heading: string;
      subHeading: string;
      description: string;
    }>;
    vision: {
      oneLiner: string;
      description: string;
    };
    voice: {
      oneLiner: string;
      description: string;
    };
    productService: string[];
    colorPalette: string[];
    suggestedFont: string;
    logo_url: string;
  };
  onSaveComplete?: () => void;
}

function BrandbookSaveAs({
  brandbook,
  onSaveComplete,
  open,
  onOpenChange,
}: BrandbookSaveAsProps) {
  const [brandbookName, setBrandbookName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const navigate = useNavigate();

  const handleSave = async () => {
    if (!isAuthenticated) {
      // Store the current brandbook in localStorage before redirecting
      localStorage.setItem('tempBrandbook', JSON.stringify(brandbook));
      
      toast.error(
        <div className="flex flex-col">
          <span>Please login or sign up to save your brandbook</span>
          <button 
            className="text-sm text-blue-500 hover:text-blue-700 underline mt-1 self-start"
            onClick={() => navigate('/auth/login')}
          >
            Go to login
          </button>
        </div>,
        { duration: 5000 }
      );
      
      onOpenChange(false);
      return;
    }

    if (!brandbookName.trim()) {
      toast.error('Please enter a brandbook name');
      return;
    }

    setIsLoading(true);
    try {
      const saveData: SaveBrandbookRequest = {
        companyName: brandbook.companyName,
        slogan: brandbook.slogan,
        brandValues: brandbook.brandValues,
        vision: brandbook.vision,
        voice: brandbook.voice,
        productService: brandbook.productService,
        colorPalette: brandbook.colorPalette,
        suggestedFont: brandbook.suggestedFont,
        logo_url: brandbook.logo_url,
      };

      const response = await brandbookService.saveBrandbook(
        brandbookName,
        saveData
      );

      if (response.success) {
        // Clear any temp brandbook from localStorage after successful save
        localStorage.removeItem('tempBrandbook');
        
        toast.success('Brandbook saved successfully');
        onOpenChange(false);
        setBrandbookName('');
        if (onSaveComplete) {
          onSaveComplete();
        }
      } else {
        toast.error(response.message || 'Failed to save brandbook');
      }
    } catch (error) {
      console.error('Failed to save brandbook:', error);
      toast.error('Failed to save brandbook. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setBrandbookName('');
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[9999]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-background rounded-lg shadow-lg p-6 w-full max-w-md flex flex-col gap-1 z-[10000] text-text-primary">
          <Dialog.Title className="text-2xl text-left font-bold text-primary">
            Save brandbook as
          </Dialog.Title>
          <div className="flex flex-col mt-2 w-full gap-4">
            <div className="flex flex-col">
              <label className="flex flex-col font-normal text-left">
                Brandbook name
                <Input
                  className="rounded-md mt-2"
                  value={brandbookName}
                  onChange={(e) => setBrandbookName(e.target.value)}
                  placeholder="Enter brandbook name"
                  autoFocus
                />
              </label>
            </div>
          </div>
          <div className="flex flex-row gap-4 mt-6 items-center justify-end max-w-lg">
            <Button
              size="sm"
              onClick={handleClose}
              variant="customSecondary"
              className="text-[14px] bg-secondary"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="customSecondary"
              className="text-[14px] bg-secondary"
              size="sm"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default BrandbookSaveAs;
